import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApi } from "@/lib/api/index.ts";

export const TRAINING_LEVEL_STATES = [
  "locked",
  "start",
  "resume",
  "completed",
] as const;

export const MENTOR_TRAINING_PROGRESS_STATUSES = [
  "not_started",
  "in_progress",
  "needs_review",
  "approved",
  "inactive",
] as const;

const trainingModuleThemeSchema = z
  .object({
    color: z.string().optional(),
    assetKey: z.string().optional(),
    pattern: z.string().optional(),
  })
  .passthrough();

const trainingModuleMetadataSchema = z
  .object({
    theme: trainingModuleThemeSchema.optional(),
  })
  .passthrough();

const trainingLevelBaseSchema = z.object({
  id: z.string(),
  position: z.number(),
  title: z.string(),
  summary: z.string().nullable(),
});

const trainingModuleBaseSchema = z.object({
  id: z.string(),
  position: z.number(),
  title: z.string(),
  description: z.string().nullable(),
  metadata: trainingModuleMetadataSchema.nullish(),
});

const trainingHomeLevelSchema = trainingLevelBaseSchema.extend({
  state: z.enum(TRAINING_LEVEL_STATES),
  attemptId: z.string().nullable(),
  isRetry: z.boolean(),
});

const trainingHomeModuleSchema = trainingModuleBaseSchema.extend({
  unlocked: z.boolean(),
  summary: z.string().nullable(),
  levels: z.array(trainingHomeLevelSchema),
});

const trainingHomeSchema = z.discriminatedUnion("enrolled", [
  z.object({ enrolled: z.literal(false) }),
  z.object({
    enrolled: z.literal(true),
    status: z.enum(MENTOR_TRAINING_PROGRESS_STATUSES),
    modules: z.array(trainingHomeModuleSchema),
    currentAttemptId: z.string().nullable(),
    nextLevelId: z.string().nullable(),
  }),
]);

export type TrainingLevelState = (typeof TRAINING_LEVEL_STATES)[number];
export type MentorTrainingProgressStatus =
  (typeof MENTOR_TRAINING_PROGRESS_STATUSES)[number];
export type TrainingModuleTheme = z.infer<typeof trainingModuleThemeSchema>;
export type TrainingModuleMetadata = z.infer<
  typeof trainingModuleMetadataSchema
>;
export type TrainingHomeLevel = z.infer<typeof trainingHomeLevelSchema>;
export type TrainingHomeModule = z.infer<typeof trainingHomeModuleSchema>;
export type TrainingHome = z.infer<typeof trainingHomeSchema>;

export interface TrainingNextLevel {
  id: string;
  position: number;
  title: string;
}

export interface TrainingNextLevelMatch {
  module: TrainingHomeModule;
  level: TrainingHomeLevel;
}

/**
 * Resolves `home.nextLevelId` to its owning module + level. Shared by the
 * session and completion screens so progression lookup lives in one place.
 */
export function findNextLevel(
  home: TrainingHome | undefined,
): TrainingNextLevelMatch | null {
  if (!home || home.enrolled !== true || !home.nextLevelId) return null;
  for (const module of home.modules) {
    const level = module.levels.find((l) => l.id === home.nextLevelId);
    if (level) return { module, level };
  }
  return null;
}

/**
 * Uses the cached module ordering to predict the level that follows a known
 * level. This is intentionally display-only: starting the next attempt still
 * resolves through the server-owned `nextLevelId`.
 */
export function findLevelAfter(
  home: TrainingHome | undefined,
  levelId: string | undefined,
): TrainingNextLevelMatch | null {
  if (!home || home.enrolled !== true || !levelId) return null;

  const levels = home.modules.flatMap((module) =>
    module.levels.map((level) => ({ module, level })),
  );
  const currentIndex = levels.findIndex(({ level }) => level.id === levelId);
  if (currentIndex < 0) return null;

  return levels[currentIndex + 1] ?? null;
}

export function findDisplayNextLevel(
  home: TrainingHome | undefined,
  currentLevelId: string | undefined,
): TrainingNextLevelMatch | null {
  const serverMatch = findNextLevel(home);
  if (serverMatch?.level.id !== currentLevelId) return serverMatch;

  return findLevelAfter(home, currentLevelId) ?? serverMatch;
}

export const trainingHomeKey = ["training-simulator", "home"] as const;
export const trainingAttemptKey = (attemptId: string | undefined) =>
  ["training-simulator", "attempt", attemptId] as const;
export const trainingAttemptMessagesKey = (attemptId: string | undefined) =>
  ["training-simulator", "attempt", attemptId, "messages"] as const;
export const trainingModuleSummaryKey = (moduleId: string | undefined) =>
  ["training-simulator", "module-summary", moduleId] as const;

export const fetchTrainingSimulatorHome = async () => {
  const { data } = await fetchApi<unknown>({
    url: "v2/training-simulator",
    method: "GET",
  });

  return trainingHomeSchema.parse(data);
};

export const useTrainingSimulatorHome = () => {
  return useQuery({
    queryKey: trainingHomeKey,
    queryFn: fetchTrainingSimulatorHome,
  });
};

// ---------------------------------------------------------------------------
// Active session (attempt) API. Unlike the home endpoint, the attempt/message
// endpoints return raw TypeORM entities in snake_case (PR #1931).
// ---------------------------------------------------------------------------

export const TRAINING_ATTEMPT_STATUSES = [
  "in_progress",
  "completed",
  "abandoned",
] as const;

export const TRAINING_MESSAGE_SENDER_TYPES = [
  "mentor",
  "simulated_teen",
  "system",
] as const;

export const TRAINING_CONVERSATION_BUCKETS = [
  "needs_response",
  "online",
  "low_engagement",
  "new",
] as const;

export const TRAINING_ATTEMPT_MODES = ["start", "resume", "retry"] as const;

export const TRAINING_COMPLETION_SCREENS = [
  "level",
  "module",
  "simulator",
] as const;

const trainingTeenProfileSchema = z
  .object({
    documentation_to_mentor: z.string().optional(),
    topics_selected: z.array(z.string()).optional(),
  })
  .passthrough();

const trainingLevelTeenSchema = z.object({
  id: z.string(),
  position: z.number(),
  display_name: z.string(),
  age: z.number(),
  gender: z.string().nullable(),
  pronouns: z.string().nullable(),
  profile: trainingTeenProfileSchema,
  conversation_bucket: z.enum(TRAINING_CONVERSATION_BUCKETS),
});

const trainingAttemptThreadSchema = z.object({
  id: z.string(),
  attempt_id: z.string(),
  level_teen_id: z.string(),
  conversation_bucket: z.enum(TRAINING_CONVERSATION_BUCKETS).nullable(),
  last_message_at: z.string().nullable(),
  // Present on the attempt response (threads.level_teen), absent on the
  // transcript response which loads bare threads.
  level_teen: trainingLevelTeenSchema.optional(),
});

const trainingCompletionPolicySchema = z.object({
  type: z.literal("mentor_message_count"),
  target: z.number(),
});

const trainingCompletionResultSchema = z.object({
  type: z.literal("mentor_message_count"),
  target: z.number(),
  mentorMessageCount: z.number(),
  satisfiedAt: z.string(),
  summary: z.string().nullish(),
});

const trainingAttemptModuleSchema = trainingModuleBaseSchema;

const trainingAttemptLevelSchema = trainingLevelBaseSchema.extend({
  module_id: z.string(),
  context_copy: z.string().nullable(),
  mentor_goal: z.string().nullable(),
  completion_policy: trainingCompletionPolicySchema,
  module: trainingAttemptModuleSchema,
});

const trainingAttemptSchema = z.object({
  id: z.string(),
  progress_id: z.string(),
  level_id: z.string(),
  status: z.enum(TRAINING_ATTEMPT_STATUSES),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  completion_result: trainingCompletionResultSchema.nullable(),
  // Authoritative completion routing from the API. Null until the attempt
  // completes; the session screen uses it to pick level modal vs full-page
  // module/simulator screens without recomputing progression on the client.
  completion_screen: z.enum(TRAINING_COMPLETION_SCREENS).nullish(),
  current_thread_id: z.string().nullable(),
  level: trainingAttemptLevelSchema,
  threads: z.array(trainingAttemptThreadSchema),
});

const trainingMessageSchema = z.object({
  id: z.string(),
  attempt_thread_id: z.string(),
  sender_type: z.enum(TRAINING_MESSAGE_SENDER_TYPES),
  sender_user_id: z.string().nullable(),
  content: z.string(),
  created_at: z.string(),
});

const trainingEventSchema = z.object({
  id: z.string(),
  attempt_id: z.string(),
  attempt_thread_id: z.string().nullable(),
  event_type: z.literal("simulated_escalation_clicked"),
  created_at: z.string(),
});

const trainingTranscriptGroupSchema = z.object({
  thread: trainingAttemptThreadSchema,
  messages: z.array(trainingMessageSchema),
  events: z.array(trainingEventSchema),
});

const trainingTranscriptSchema = z.array(trainingTranscriptGroupSchema);

export type TrainingAttemptStatus = (typeof TRAINING_ATTEMPT_STATUSES)[number];
export type TrainingMessageSenderType =
  (typeof TRAINING_MESSAGE_SENDER_TYPES)[number];
export type TrainingConversationBucket =
  (typeof TRAINING_CONVERSATION_BUCKETS)[number];
export type TrainingAttemptMode = (typeof TRAINING_ATTEMPT_MODES)[number];
export type TrainingCompletionScreen =
  (typeof TRAINING_COMPLETION_SCREENS)[number];
export type TrainingLevelTeen = z.infer<typeof trainingLevelTeenSchema>;
export type TrainingAttemptThread = z.infer<typeof trainingAttemptThreadSchema>;
export type TrainingAttempt = z.infer<typeof trainingAttemptSchema>;
export type TrainingMessage = z.infer<typeof trainingMessageSchema>;
export type TrainingEvent = z.infer<typeof trainingEventSchema>;
export type TrainingTranscriptGroup = z.infer<
  typeof trainingTranscriptGroupSchema
>;

export const useTrainingAttempt = (attemptId: string | undefined) => {
  return useQuery({
    queryKey: trainingAttemptKey(attemptId),
    enabled: !!attemptId,
    queryFn: async () => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/attempts/${attemptId}`,
        method: "GET",
      });

      return trainingAttemptSchema.parse(data);
    },
  });
};

export const useTrainingAttemptMessages = (attemptId: string | undefined) => {
  return useQuery({
    queryKey: trainingAttemptMessagesKey(attemptId),
    enabled: !!attemptId,
    queryFn: async () => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/attempts/${attemptId}/messages`,
        method: "GET",
      });

      return trainingTranscriptSchema.parse(data);
    },
  });
};

const trainingModuleSummarySchema = z.object({
  summary: z.string(),
});

export type TrainingModuleSummary = z.infer<typeof trainingModuleSummarySchema>;

/**
 * Fetches a module's completion summary. The API generates it lazily on the
 * first request and returns the cached result thereafter, so a cold module can
 * be slow — the Module Complete screen shows a loading state until it resolves.
 */
export const useTrainingModuleSummary = (moduleId: string | undefined) => {
  return useQuery({
    queryKey: trainingModuleSummaryKey(moduleId),
    enabled: !!moduleId,
    queryFn: async () => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/modules/${moduleId}/summary`,
        method: "GET",
      });

      return trainingModuleSummarySchema.parse(data);
    },
  });
};

export const useStartTrainingAttempt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["training-simulator", "start-attempt"],
    mutationFn: async ({
      levelId,
      mode,
    }: {
      levelId: string;
      mode: TrainingAttemptMode;
    }) => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/levels/${levelId}/attempts`,
        method: "POST",
        data: { mode },
      });

      return trainingAttemptSchema.parse(data);
    },
    onSuccess: (attempt) => {
      console.log("attempt", attempt);
      queryClient.setQueryData(trainingAttemptKey(attempt.id), attempt);
      queryClient.invalidateQueries({ queryKey: trainingHomeKey });
    },
  });
};

export const useSendTrainingMessage = (attemptId: string, threadId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["training-simulator", "send-message", attemptId, threadId],
    mutationFn: async ({ content }: { content: string }) => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/attempts/${attemptId}/threads/${threadId}/messages`,
        method: "POST",
        data: { content },
      });

      return trainingMessageSchema.parse(data);
    },
    onMutate: async ({ content }) => {
      const key = trainingAttemptMessagesKey(attemptId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<TrainingTranscriptGroup[]>(key);

      const optimisticMessage: TrainingMessage = {
        id: `optimistic-${Date.now()}`,
        attempt_thread_id: threadId,
        sender_type: "mentor",
        sender_user_id: null,
        content,
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData<TrainingTranscriptGroup[]>(key, (groups) => {
        if (!groups || groups.length === 0) return groups;
        return groups.map((group) =>
          group.thread.id === threadId
            ? { ...group, messages: [...group.messages, optimisticMessage] }
            : group,
        );
      });

      return { previous, optimisticId: optimisticMessage.id };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          trainingAttemptMessagesKey(attemptId),
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: trainingAttemptMessagesKey(attemptId),
      });
    },
  });
};

const leadMentorMessageSchema = z.object({
  channelId: z.string(),
  message: z
    .object({
      id: z.string(),
      message: z.string(),
      created_at: z.string(),
    })
    .passthrough(),
});

export type TrainingLeadMentorMessage = z.infer<typeof leadMentorMessageSchema>;

/**
 * Sends a plain-text support message from the mentor to their assigned Lead
 * Mentor over the existing internal-ops Sendbird channel. Passing `attemptId`
 * lets the API tag the message with the module/level the mentor was on.
 */
export const useSendLeadMentorMessage = (attemptId?: string) => {
  return useMutation({
    mutationKey: ["training-simulator", "lead-mentor-message", attemptId],
    mutationFn: async ({ message }: { message: string }) => {
      const { data } = await fetchApi<unknown>({
        url: "v2/training-simulator/lead-mentor-message",
        method: "POST",
        data: { message, ...(attemptId ? { attemptId } : {}) },
      });

      return leadMentorMessageSchema.parse(data);
    },
  });
};

export const useEndTrainingSession = (attemptId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["training-simulator", "end-session", attemptId],
    mutationFn: async () => {
      await fetchApi<unknown>({
        url: `v2/training-simulator/attempts/${attemptId}/end-session`,
        method: "POST",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingHomeKey });
    },
  });
};

export const useRecordTrainingEvent = (attemptId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["training-simulator", "record-event", attemptId],
    mutationFn: async ({
      eventType,
      threadId,
    }: {
      eventType: "simulated_escalation_clicked";
      threadId: string;
    }) => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/attempts/${attemptId}/events`,
        method: "POST",
        data: { eventType, threadId },
      });

      return trainingEventSchema.parse(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trainingAttemptMessagesKey(attemptId),
      });
    },
  });
};

export const useResetAttempt = (attemptId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["training-simulator", "reset-attempt", attemptId],
    mutationFn: async () => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/attempts/${attemptId}/reset`,
        method: "POST",
      });

      return trainingAttemptSchema.parse(data);
    },
    onSuccess: (attempt) => {
      queryClient.setQueryData(trainingAttemptKey(attemptId), attempt);
      // Returned so the mutation awaits the transcript refetch, letting callers
      // keep a loader up until the cleared conversation is actually visible.
      return queryClient.invalidateQueries({
        queryKey: trainingAttemptMessagesKey(attemptId),
      });
    },
  });
};
